import Pusher from "pusher-js/with-encryption";
import store from '@/store'

export const CHANNEL = Object.freeze({
  FOR_USER: ({ env, user }) => {
    return `private-encrypted-ld-${env}-user-${user.id}`
  },
  FOR_HOSPITAL: ({ env, hospital }) => {
    return `private-encrypted-ld-${env}-hospital-${hospital.id}`
  },
  FOR_HOSPITAL_NUDGE: ({ env, hospital }) => {
    return `private-encrypted-ld-${env}-watch-nudge-hospital-${hospital.id}`
  },
  FOR_DEVICE: ({ env, deviceId }) => {
    return `private-encrypted-ld-${env}-device-${deviceId}`
  },
});

export const CLIENT_EVENT = Object.freeze({
  LOGOUT_WITH_EXPIRATION: 'client-logout-with-expiration',
  SESSION_EXPIRED: 'client-session-expired',
  CURTAIN_TOGGLE: 'client-virtual-curtain-toggled',
  MIC_TOGGLED: 'client-patient-monitor-microphone-toggled',
  LISTEN_TOGGLED: 'client-patient-monitor-listen-toggled',
  ROOM_NOTIFICATION: 'client-room-notification',
  USER_AUTHENTICATED: 'client-user-authenticated',
  ROOM_NOTIFICATION_ACCEPTED: 'client-room-notification-accepted',
  VIDEO_CONNECTION_SUCCESSFUL: 'client-video-connection-successful',
});
export const SERVER_EVENT = Object.freeze({
  USER_UPDATED: 'user-updated',
  CURTAIN_TOGGLED: 'patient-monitor-curtain-toggled',
  USER_ASSIGNMENTS_UPDATED: 'user-assignments-updated',
  DEVICE_STATUS_CHANGED: 'device-status-changed',
  WIFI_STATS_UPDATED: 'wifi-stats-updated',
  REQUEST_BROWSER_REFRESH: 'request-browser-refresh',
  PATIENT_MONITOR_UPDATED: 'patient-monitor-updated',
  PATIENT_MONITOR_TRANSFER_CREATED: 'patient-monitor-transfer-created',
  PATIENT_MONITOR_TRANSFER_ACCEPTED: 'patient-monitor-transfer-accepted',
  PATIENT_MONITOR_TRANSFER_SUCCESSFUL: 'patient-monitor-transfer-successful',
  PATIENT_MONITOR_TRANSFER_CANCELLED: 'patient-monitor-transfer-cancelled',
  TRANSFER_NEEDS_ACKNOWLEDGMENT: 'transfer-needs-acknowledgment',
  ASSIGNMENTS_UPDATED_EXTERNAL: 'assignments-updated-external',
  PATIENT_EVENTS_UPDATED: 'patient-events-updated',
  MEETING_STATUS_CHANGED: 'meeting-status-changed',
  VIDEO_RECORDING_AVAILABLE: 'video-recording-available',
  NUDGE_STATE_CHANGED: 'nudge-state-changed',
  MY_HOME_TRANSFER: 'my-home-transfer',
  RECORDING_ANALYSIS_COMPLETED: 'recording-analysis-completed',
  WAKE_WORD_DETECTED: 'wake-word-detected',
  VIDEO_BANNER_ACTIVATED: 'video-banner-activated',
  AI_SNAPSHOT_UPDATED: 'ai-snapshot-updated',
  KIOSK_JOIN_REQUEST: 'kiosk-join-request',
  KIOSK_SIGNAL: 'kiosk-signal',
  AIMEE_START: 'aimee-start',
  AIMEE_CHAT_CHUNK: 'aimee-chat-chunk',
  SCHEDULED_FORM_RUN_UPDATED: 'scheduled-form-run-updated'
})

export default {
  install: function (app, opts) {
    let pusher = null
    const buildOptions = () => ({
      ...opts.options,
      channelAuthorization: {
        transport: 'ajax',
        endpoint: `${opts.options.authEndpoint}?division_id=*`,
        // called on every auth request, so reconnects use the current token
        // instead of the one captured at page load (LD-2458)
        headersProvider: () => ({ authorization: `Bearer ${store.state.token}` })
      }
    })
    const retryDelays = {}
    // pusher-js gives up on a channel after one failed auth; retry with capped backoff
    const watchSubscription = (channel) => {
      if (channel.retryWatched) return channel
      channel.retryWatched = true
      channel.bind('pusher:subscription_succeeded', () => {
        if (retryDelays[channel.name]) {
          console.warn(`[pusher] subscription recovered on ${channel.name}`)
          delete retryDelays[channel.name]
        }
      })
      channel.bind('pusher:subscription_error', (error) => {
        console.warn(`[pusher] subscription error on ${channel.name}`, error)
        retryDelays[channel.name] = Math.min((retryDelays[channel.name] || 1000) * 2, 60000)
        setTimeout(() => {
          if (pusher.channel(channel.name)) pusher.subscribe(channel.name)
        }, retryDelays[channel.name])
      })
      return channel
    }
    // console.warn so connection health shows up in LogRocket session replays
    const watchConnection = () => {
      let downSince = null
      let reconnectDelay = 0
      pusher.connection.bind('state_change', ({ previous, current }) => {
        console.warn(`[pusher] connection: ${previous} -> ${current}`)
        if (['unavailable', 'failed', 'disconnected'].includes(current) && !downSince) {
          downSince = Date.now()
        }
        if (current == 'connected') {
          if (downSince) {
            console.warn(`[pusher] connection recovered after ${Math.round((Date.now() - downSince) / 1000)}s`)
            downSince = null
          }
          reconnectDelay = 0
        } else if (current == 'disconnected') {
          // the app never calls disconnect(): this state means the server refused the
          // connection (close codes 4000-4099) and pusher-js won't reconnect on its own
          reconnectDelay = Math.min((reconnectDelay || 2500) * 2, 60000)
          setTimeout(() => {
            if (pusher.connection.state == 'disconnected') pusher.connect()
          }, reconnectDelay)
        }
      })
      pusher.connection.bind('error', (error) => console.warn('[pusher] connection error', error))
    }
    const createPusher = () => {
      pusher = new Pusher(opts.appKey, buildOptions())
      const subscribe = pusher.subscribe.bind(pusher)
      pusher.subscribe = (name) => watchSubscription(subscribe(name))
      watchConnection()
    }
    if (store.state.token) {
      createPusher()
    } else {
      const unsubscribe = store.subscribe((mutation) => {
        if (mutation.type == 'setToken') {
          createPusher()
          unsubscribe()
        }
      })
    }
    /**
     * @type Pusher
     */
    Object.defineProperty(app.config.globalProperties, '$pusher', {
      get() {
        if (!pusher) {
          throw new Error("$pusher called before auth token was set")
        }
        return pusher
      }
    })
  }
}
